/**
 * Demo Page Wrapper
 * 
 * This component checks if the current tenant is the demo tenant (Quantum Leap Labs)
 * and renders the appropriate page:
 * - Demo tenant → Demo page (hardcoded data, no API calls)
 * - Real tenant → Real page (actual API calls)
 * 
 * This approach keeps demo functionality completely isolated from production code.
 */
import { useMemo } from "react";

const DEMO_TENANT_ID = "demo-tenant-00000000-0000-0000-0000-000000000001";

/**
 * Check if currently viewing the demo tenant
 */
export function isDemoTenant() {
  const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
  const userStr = sessionStorage.getItem("user");
  
  // If viewing a specific tenant, check that
  if (viewingTenantId) {
    return viewingTenantId === DEMO_TENANT_ID;
  }
  
  // Otherwise check user's home tenant
  try {
    const user = JSON.parse(userStr || "{}");
    const memberships = user.tenant_memberships || [];
    if (memberships.length > 0) {
      const firstTenant = typeof memberships[0] === 'string' 
        ? memberships[0] 
        : memberships[0]?.tenant_id;
      return firstTenant === DEMO_TENANT_ID;
    }
  } catch (e) {
    console.error("Error parsing user data:", e);
  }
  
  return false;
}

/**
 * Hook to check if currently in demo mode
 */
export function useDemoMode() {
  return useMemo(() => isDemoTenant(), []);
}

/**
 * Wrapper component that renders demo or real page based on tenant
 * 
 * Usage:
 * <DemoPageWrapper 
 *   DemoComponent={DemoWebsiteTrafficPage} 
 *   RealComponent={WebsiteTrafficPage} 
 * />
 */
export function DemoPageWrapper({ DemoComponent, RealComponent, ...props }) {
  const isDemo = useDemoMode();
  
  if (isDemo) {
    return <DemoComponent {...props} />;
  }
  
  return <RealComponent {...props} />;
}

export default DemoPageWrapper;
